/**
 * PreviewPanel - selected model detail card shown below the trigger button.
 *
 * Renders only when a model with image_url or description is selected and
 * the dropdown is closed.
 */

import { memo, useCallback } from 'react';
import { Download, Heart, Star, ExternalLink } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { ImageModelInfo } from '@/types';
import { ORIENTATION_LABELS, deriveOrientation, formatCount } from './modelSelectorUtils';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PreviewPanelProps {
  model: ImageModelInfo;
  brokenImages: Set<string>;
  onImageBroken: (key: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PreviewPanel = memo(function PreviewPanel({
  model,
  brokenImages,
  onImageBroken,
}: PreviewPanelProps) {
  const { t } = useTranslation();

  const imageKey = model.image_url || model.id;
  const showImage = Boolean(model.image_url) && !brokenImages.has(imageKey);

  const handleImageError = useCallback(() => {
    onImageBroken(imageKey);
  }, [onImageBroken, imageKey]);

  return (
    <div className="mt-2 flex gap-3 p-3 bg-black/20 border border-white/10 rounded-lg">
      {showImage && (
        <img
          src={model.image_url as string}
          alt=""
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover flex-shrink-0 bg-black/30"
          loading="lazy"
          onError={handleImageError}
        />
      )}

      <div className="flex-1 min-w-0">
        {/* Name + training badges (orientation derived from training resolution) */}
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-medium text-white truncate">{model.name}</h4>
          {(() => {
            if (!model.training_resolution) {
              return (
                <span className="text-[10px] bg-white/5 text-white/50 px-1.5 py-0.5 rounded flex-shrink-0 italic">
                  Multi-aspect {model.base}
                </span>
              );
            }
            const orientation = deriveOrientation(model.training_resolution);
            return (
              <>
                {orientation && (
                  <span className="text-[10px] bg-blue-900/40 text-blue-300 px-1.5 py-0.5 rounded flex-shrink-0">
                    {ORIENTATION_LABELS[orientation]}
                  </span>
                )}
                <span className="text-[10px] bg-purple-900/40 text-purple-200 px-1.5 py-0.5 rounded flex-shrink-0 font-mono">
                  {model.training_resolution}
                </span>
              </>
            );
          })()}
          {model.nsfw && (
            <span className="text-[10px] bg-red-600/20 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
              {t('image.model.nsfwBadge', { defaultValue: 'NSFW' })}
            </span>
          )}
          {model.is_installed === false && (
            <span className="text-[10px] bg-white/5 text-white/40 border border-white/10 px-1.5 py-0.5 rounded flex-shrink-0">
              {t('deforum.picker.checkpoint.notInstalled')}
            </span>
          )}
        </div>

        {/* Creator */}
        {model.creator && (
          <p className="text-[11px] text-white/40 mt-0.5">
            {t('image.model.by', { defaultValue: 'by' })} {model.creator}
          </p>
        )}

        {/* Description */}
        {model.description && (
          <p className="text-xs text-white/50 mt-1 line-clamp-2">{model.description}</p>
        )}

        {/* Training metadata: full bucket list + author notes (resolution itself is in the badges above) */}
        {((model.recommended_aspects && model.recommended_aspects.length > 1) ||
          model.training_notes) && (
          <div className="mt-1.5 space-y-0.5">
            {model.recommended_aspects && model.recommended_aspects.length > 1 && (
              <p className="text-[11px] text-white/50">
                <span className="text-white/40">
                  {t('image.model.recommendedAspects', {
                    defaultValue: 'Multi-aspect support',
                  })}
                  :
                </span>{' '}
                <span className="font-mono text-white/60">
                  {model.recommended_aspects.join(', ')}
                </span>
              </p>
            )}
            {model.training_notes && (
              <p className="text-[11px] text-white/40 italic line-clamp-2">
                {model.training_notes}
              </p>
            )}
          </div>
        )}

        {/* Stats row + CivitAI link. The link must NOT be gated on `stats` —
            consumers without live CivitAI search results (e.g. the Deforum
            picker, which always passes stats: null) still have a civitai_id
            and must still be able to open the model page. */}
        {(model.stats || model.civitai_id) && (
          <div className="flex items-center gap-3 mt-1.5">
            {model.stats && (
              <>
                <span className="inline-flex items-center gap-1 text-[11px] text-white/40">
                  <Download className="h-3 w-3" />
                  {formatCount(model.stats.downloadCount)}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-white/40">
                  <Heart className="h-3 w-3" />
                  {formatCount(model.stats.favoriteCount)}
                </span>
                {model.stats.rating > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-300">
                    <Star className="h-3 w-3" />
                    {model.stats.rating.toFixed(1)}
                  </span>
                )}
              </>
            )}
            {model.civitai_id && (
              <a
                href={`https://civitai.com/models/${model.civitai_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-primary-400 hover:text-primary-300 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                CivitAI
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default PreviewPanel;
